import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/App/hooks';
import { getTranslationMapThunk } from '@/features/i18n/i18nSlice';
import { selectLocale } from '@/features/i18n/selectLocale';
import { selectTranslationsInitialized } from '@/features/i18n/selectTranslationsInitialized';
import { getLanguageReferenceDataThunk } from '@/features/refdata/refdataSlice';
import { TranslationProvider } from '@/hooks/TranslationProvider';

import { selectTranslationEntries } from './selectTranslationEntries';

export const withTranslations = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
): React.ComponentType<P> => {
  const HOC = (props: P) => {
    const dispatch = useAppDispatch();
    const locale = useAppSelector(selectLocale);
    const translationsInitialized = useAppSelector(selectTranslationsInitialized);
    const translationEntries = useAppSelector(selectTranslationEntries);

    useEffect(() => {
      if (!translationsInitialized && locale) {
        dispatch(getLanguageReferenceDataThunk());
        dispatch(getTranslationMapThunk(locale));
      }
    }, [dispatch, translationsInitialized, locale]);

    if (!translationsInitialized || !locale) {
      return null;
    }

    return (
      <TranslationProvider translations={translationEntries ?? {}}>
        <WrappedComponent {...props} />
      </TranslationProvider>
    );
  };

  HOC.displayName = `withTranslations(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return HOC;
};
